import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CardGiftcard,
  Group,
  Insights,
  Search,
  Send,
  PlaylistAddCheck,
  Edit,
  Delete,
  Add,
  Close
} from '@mui/icons-material';
import axios from 'axios';

interface DriverSummary {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  driverRating?: number;
  totalTripsAsDriver?: number;
  status?: string;
}

interface GrantRecord {
  id: string;
  driver: DriverSummary;
  credits: number;
  grantedAt: string;
}

interface OverviewData {
  driverStats: {
    totalDrivers: number;
    activeDrivers: number;
  };
  promotionStats: {
    totalGrants: number;
    totalCreditsGranted: number;
  };
  recentGrants: GrantRecord[];
}

interface CreditPackageConfig {
  id: string;
  name: string;
  rideCount: number;
  discountPercentage: number;
  finalPrice: number;
  originalPrice: number;
  pricePerRide: number;
  savings: number;
  popular: boolean;
  description?: string;
  displayOrder?: number;
}

interface PackageFormData {
  name: string;
  rideCount: number;
  discountPercentage: number;
  description: string;
  isPopular: boolean;
}

const CreditPromotionsPage: React.FC = () => {
  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  const [overview, setOverview] = useState<OverviewData>({
    driverStats: { totalDrivers: 0, activeDrivers: 0 },
    promotionStats: { totalGrants: 0, totalCreditsGranted: 0 },
    recentGrants: []
  });
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<DriverSummary[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<DriverSummary | null>(null);
  const [singleCredits, setSingleCredits] = useState(5);
  const [singleReason, setSingleReason] = useState('');
  const [singleExpiresAt, setSingleExpiresAt] = useState('');

  const [isSearchingDrivers, setIsSearchingDrivers] = useState(false);
  const [isGrantingSingle, setIsGrantingSingle] = useState(false);

  const [bulkCredits, setBulkCredits] = useState(5);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkMinRating, setBulkMinRating] = useState('');
  const [bulkMinTrips, setBulkMinTrips] = useState('');
  const [bulkExpiresAt, setBulkExpiresAt] = useState('');
  const [bulkOnlyActive, setBulkOnlyActive] = useState(true);
  const [bulkPreview, setBulkPreview] = useState<{ totalMatches: number; sampleDrivers: DriverSummary[] }>({
    totalMatches: 0,
    sampleDrivers: []
  });
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isGrantingBulk, setIsGrantingBulk] = useState(false);

  const [baseRate, setBaseRate] = useState(1);
  const [baseRateInput, setBaseRateInput] = useState('1.00');
  const [pricingPackages, setPricingPackages] = useState<CreditPackageConfig[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [savingBaseRate, setSavingBaseRate] = useState(false);

  // Package management state
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackageConfig | null>(null);
  const [packageForm, setPackageForm] = useState<PackageFormData>({
    name: '',
    rideCount: 5,
    discountPercentage: 0,
    description: '',
    isPopular: false
  });
  const [savingPackage, setSavingPackage] = useState(false);
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null);

  useEffect(() => {
    fetchOverview();
    fetchPricingConfig();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      Authorization: `Bearer ${token}`
    };
  };

  const handleApiError = (error: any, fallbackMessage: string) => {
    console.error(fallbackMessage, error);
    const message = error?.response?.data?.message || fallbackMessage;
    setAlert({ type: 'error', message });
  };

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/credits/overview`, {
        headers: getAuthHeaders()
      });
      if (response.data?.success) {
        setOverview(response.data.data);
      }
    } catch (error) {
      handleApiError(error, 'Failed to load credit overview');
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchPricingConfig = async () => {
    setPricingLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/credits/config`, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        const config = response.data.data;
        if (config?.baseRate) {
          setBaseRate(config.baseRate);
          setBaseRateInput(config.baseRate.toFixed(2));
        }
        if (config?.packages) {
          setPricingPackages(config.packages);
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load credit pricing');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleSaveBaseRate = async () => {
    const normalized = parseFloat(baseRateInput);
    if (Number.isNaN(normalized) || normalized <= 0) {
      setAlert({ type: 'info', message: 'Enter a positive base rate.' });
      return;
    }

    setSavingBaseRate(true);
    try {
      const response = await axios.put(`${apiUrl}/admin/credits/config`, { baseRate: normalized }, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        setBaseRate(response.data.data.baseRate);
        setPricingPackages(response.data.data.packages || []);
        setAlert({ type: 'success', message: 'Credit pricing updated successfully.' });
      }
    } catch (error) {
      handleApiError(error, 'Failed to update credit pricing');
    } finally {
      setSavingBaseRate(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(value);
  };

  // Package management functions
  const openCreatePackageDialog = () => {
    setEditingPackage(null);
    setPackageForm({
      name: '',
      rideCount: 5,
      discountPercentage: 0,
      description: '',
      isPopular: false
    });
    setPackageDialogOpen(true);
  };

  const openEditPackageDialog = (pkg: CreditPackageConfig) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      rideCount: pkg.rideCount,
      discountPercentage: pkg.discountPercentage * 100, // Convert to percentage for display
      description: pkg.description || '',
      isPopular: pkg.popular
    });
    setPackageDialogOpen(true);
  };

  const closePackageDialog = () => {
    setPackageDialogOpen(false);
    setEditingPackage(null);
  };

  const handleSavePackage = async () => {
    if (!packageForm.name.trim()) {
      setAlert({ type: 'info', message: 'Package name is required.' });
      return;
    }
    if (packageForm.rideCount <= 0) {
      setAlert({ type: 'info', message: 'Credits must be a positive number.' });
      return;
    }

    setSavingPackage(true);
    try {
      const payload = {
        name: packageForm.name.trim(),
        rideCount: packageForm.rideCount,
        discountPercentage: packageForm.discountPercentage / 100, // Convert back to decimal
        description: packageForm.description,
        isPopular: packageForm.isPopular
      };

      let response;
      if (editingPackage) {
        // Update existing package
        response = await axios.put(
          `${apiUrl}/admin/credits/packages/${editingPackage.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new package
        response = await axios.post(
          `${apiUrl}/admin/credits/packages`,
          payload,
          { headers: getAuthHeaders() }
        );
      }

      if (response.data?.success) {
        setPricingPackages(response.data.data.packages || []);
        setAlert({
          type: 'success',
          message: editingPackage ? 'Package updated successfully.' : 'Package created successfully.'
        });
        closePackageDialog();
      }
    } catch (error) {
      handleApiError(error, 'Failed to save package');
    } finally {
      setSavingPackage(false);
    }
  };

  const handleDeletePackage = async (pkg: CreditPackageConfig) => {
    if (!window.confirm(`Are you sure you want to delete "${pkg.name}"? This cannot be undone.`)) {
      return;
    }

    setDeletingPackageId(pkg.id);
    try {
      const response = await axios.delete(
        `${apiUrl}/admin/credits/packages/${pkg.id}`,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        setPricingPackages(response.data.data.packages || []);
        setAlert({ type: 'success', message: response.data.message });
      }
    } catch (error) {
      handleApiError(error, 'Failed to delete package');
    } finally {
      setDeletingPackageId(null);
    }
  };

  const searchDrivers = async () => {
    if (!searchTerm.trim()) {
      setAlert({ type: 'info', message: 'Enter a name, email, or phone to search for drivers.' });
      return;
    }

    setIsSearchingDrivers(true);
    try {
      const params = new URLSearchParams({
        search: searchTerm.trim(),
        role: 'DRIVER',
        limit: '10'
      });

      const response = await axios.get(`${apiUrl}/admin/users?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        setSearchResults(response.data.users || []);
        if ((response.data.users || []).length === 0) {
          setAlert({ type: 'info', message: 'No drivers found with that search.' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to search drivers');
    } finally {
      setIsSearchingDrivers(false);
    }
  };

  const handleSingleGrant = async () => {
    if (!selectedDriver) {
      setAlert({ type: 'info', message: 'Select a driver to grant credits.' });
      return;
    }

    if (!singleCredits || singleCredits <= 0) {
      setAlert({ type: 'info', message: 'Enter a positive credit amount.' });
      return;
    }

    setIsGrantingSingle(true);
    try {
      const payload = {
        driverId: selectedDriver.id,
        credits: singleCredits,
        reason: singleReason,
        expiresAt: singleExpiresAt || undefined
      };

      const response = await axios.post(`${apiUrl}/admin/credits/grant`, payload, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        setAlert({ type: 'success', message: `Granted ${singleCredits} credits to ${selectedDriver.preferredName || selectedDriver.firstName}.` });
        setSearchResults([]);
        setSelectedDriver(null);
        setSingleReason('');
        setSingleExpiresAt('');
        fetchOverview();
      }
    } catch (error) {
      handleApiError(error, 'Failed to grant credits');
    } finally {
      setIsGrantingSingle(false);
    }
  };

  const previewBulkRecipients = async () => {
    setIsPreviewing(true);
    try {
      const payload = {
        filters: {
          minRating: bulkMinRating ? parseFloat(bulkMinRating) : undefined,
          minTrips: bulkMinTrips ? parseInt(bulkMinTrips, 10) : undefined,
          onlyActive: bulkOnlyActive
        }
      };

      const response = await axios.post(`${apiUrl}/admin/credits/grant/preview`, payload, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        setBulkPreview(response.data.data);
      }
    } catch (error) {
      handleApiError(error, 'Failed to preview recipients');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleBulkGrant = async () => {
    if (!bulkCredits || bulkCredits <= 0) {
      setAlert({ type: 'info', message: 'Enter a positive credit amount for the promotion.' });
      return;
    }

    if (bulkPreview.totalMatches === 0) {
      setAlert({ type: 'info', message: 'Preview recipients before sending a bulk promotion.' });
      return;
    }

    if (!window.confirm(`Send ${bulkCredits} credits to ${bulkPreview.totalMatches} drivers?`)) {
      return;
    }

    setIsGrantingBulk(true);
    try {
      const payload = {
        credits: bulkCredits,
        reason: bulkReason,
        expiresAt: bulkExpiresAt || undefined,
        filters: {
          minRating: bulkMinRating ? parseFloat(bulkMinRating) : undefined,
          minTrips: bulkMinTrips ? parseInt(bulkMinTrips, 10) : undefined,
          onlyActive: bulkOnlyActive
        }
      };

      const response = await axios.post(`${apiUrl}/admin/credits/grant/bulk`, payload, {
        headers: getAuthHeaders()
      });

      if (response.data?.success) {
        setAlert({ type: 'success', message: response.data.message });
        fetchOverview();
      }
    } catch (error) {
      handleApiError(error, 'Failed to send bulk promotion');
    } finally {
      setIsGrantingBulk(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    icon
  }: {
    title: string;
    value: string | number;
    subtitle: string;
    icon: React.ReactNode;
  }) => (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main'
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {loadingOverview ? <CircularProgress size={20} /> : value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        Credit Promotions
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Reward top drivers with promotional credits to keep them engaged.
      </Typography>

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Credit Pricing
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            onClick={openCreatePackageDialog}
            size="small"
          >
            Add Package
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Base price per credit (CAD)"
            type="number"
            inputProps={{ min: 0.1, step: 0.01 }}
            value={baseRateInput}
            onChange={(e) => setBaseRateInput(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveBaseRate}
            disabled={savingBaseRate}
          >
            {savingBaseRate ? 'Saving...' : 'Save'}
          </Button>
        </Stack>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Current base rate: {formatCurrency(baseRate)}
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Package</TableCell>
              <TableCell>Credits</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Savings</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(pricingPackages || []).map(pkg => (
              <TableRow key={pkg.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={pkg.popular ? 'bold' : 'medium'}>
                    {pkg.name} {pkg.popular ? '🔥' : ''}
                  </Typography>
                </TableCell>
                <TableCell>{pkg.rideCount}</TableCell>
                <TableCell>{pkg.discountPercentage ? `${Math.round(pkg.discountPercentage * 100)}%` : '—'}</TableCell>
                <TableCell>{formatCurrency(pkg.finalPrice)}</TableCell>
                <TableCell>{pkg.savings > 0 ? formatCurrency(pkg.savings) : '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit package">
                    <IconButton
                      size="small"
                      onClick={() => openEditPackageDialog(pkg)}
                      color="primary"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete package">
                    <IconButton
                      size="small"
                      onClick={() => handleDeletePackage(pkg)}
                      color="error"
                      disabled={deletingPackageId === pkg.id}
                    >
                      {deletingPackageId === pkg.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Delete fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!pricingPackages.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  {pricingLoading ? 'Loading packages...' : 'No packages available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Total Promotional Credits"
            value={`${overview.promotionStats.totalCreditsGranted || 0} credits`}
            subtitle="Awarded to date"
            icon={<CardGiftcard />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Drivers Awarded"
            value={overview.promotionStats.totalGrants || 0}
            subtitle="Individual grants recorded"
            icon={<Group />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Active Drivers"
            value={`${overview.driverStats.activeDrivers}/${overview.driverStats.totalDrivers}`}
            subtitle="Active / total drivers"
            icon={<Insights />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search fontSize="small" />
              Single Driver Grant
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Search drivers (name, email, phone)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={searchDrivers}
                disabled={isSearchingDrivers}
                startIcon={isSearchingDrivers ? <CircularProgress size={18} /> : <Search />}
              >
                {isSearchingDrivers ? 'Searching...' : 'Search Drivers'}
              </Button>

              {searchResults.length > 0 && (
                <Paper variant="outlined">
                  <List dense>
                    {searchResults.map((driver) => (
                      <ListItem
                        key={driver.id}
                        disablePadding
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={`${driver.driverRating?.toFixed(1) || '0.0'}★`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={`${driver.totalTripsAsDriver || 0} trips`}
                              size="small"
                              color="default"
                            />
                          </Stack>
                        }
                      >
                        <ListItemButton
                          selected={selectedDriver?.id === driver.id}
                          onClick={() => setSelectedDriver(driver)}
                        >
                          <ListItemText
                            primary={`${driver.preferredName || driver.firstName || ''} ${driver.lastName ? driver.lastName.charAt(0) + '.' : ''}`}
                            secondary={driver.email}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {selectedDriver && (
                <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedDriver.preferredName || selectedDriver.firstName} {selectedDriver.lastName ? selectedDriver.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDriver.email}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label={`${selectedDriver.driverRating?.toFixed(1) || '0.0'}★`} color="primary" size="small" />
                    <Chip label={`${selectedDriver.totalTripsAsDriver || 0} trips`} size="small" />
                  </Stack>
                </Box>
              )}

              <TextField
                label="Credits to grant"
                type="number"
                value={singleCredits}
                onChange={(e) => setSingleCredits(parseInt(e.target.value, 10))}
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Reason (optional)"
                value={singleReason}
                onChange={(e) => setSingleReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Expires On (optional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={singleExpiresAt}
                onChange={(e) => setSingleExpiresAt(e.target.value)}
              />

              <Button
                variant="contained"
                color="primary"
                startIcon={isGrantingSingle ? <CircularProgress size={18} color="inherit" /> : <Send />}
                onClick={handleSingleGrant}
                disabled={isGrantingSingle}
              >
                {isGrantingSingle ? 'Granting...' : 'Grant Credits'}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaylistAddCheck fontSize="small" />
              Bulk Promotions
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Credits per driver"
                type="number"
                value={bulkCredits}
                onChange={(e) => setBulkCredits(parseInt(e.target.value, 10))}
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Minimum rating (e.g., 4.8)"
                type="number"
                value={bulkMinRating}
                onChange={(e) => setBulkMinRating(e.target.value)}
                fullWidth
              />
              <TextField
                label="Minimum completed trips"
                type="number"
                value={bulkMinTrips}
                onChange={(e) => setBulkMinTrips(e.target.value)}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkOnlyActive}
                    onChange={(e) => setBulkOnlyActive(e.target.checked)}
                  />
                }
                label="Only include active drivers"
              />
              <TextField
                label="Promotion reason (optional)"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Expires On (optional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={bulkExpiresAt}
                onChange={(e) => setBulkExpiresAt(e.target.value)}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={isPreviewing ? <CircularProgress size={18} /> : <Search />}
                  onClick={previewBulkRecipients}
                  disabled={isPreviewing}
                  fullWidth
                >
                  {isPreviewing ? 'Previewing...' : 'Preview Recipients'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isGrantingBulk ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  onClick={handleBulkGrant}
                  disabled={isGrantingBulk}
                  fullWidth
                >
                  {isGrantingBulk ? 'Sending...' : 'Send Promotion'}
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bulkPreview.totalMatches} drivers match your criteria
                </Typography>
                <Divider sx={{ my: 1 }} />
                {bulkPreview.sampleDrivers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Run a preview to see sample drivers.
                  </Typography>
                ) : (
                  <List dense>
                    {bulkPreview.sampleDrivers.map(driver => (
                      <ListItem key={driver.id}>
                        <ListItemText
                          primary={`${driver.preferredName || driver.firstName || ''} ${driver.lastName ? driver.lastName.charAt(0) + '.' : ''}`}
                          secondary={`${driver.email} • Rating ${driver.driverRating?.toFixed(1) || '0.0'} • ${driver.totalTripsAsDriver || 0} trips`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Recent Grants
        </Typography>

        {overview.recentGrants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No promotional grants have been recorded yet.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Driver</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.recentGrants.map(grant => (
                <TableRow key={grant.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {grant.driver.preferredName || grant.driver.firstName} {grant.driver.lastName ? grant.driver.lastName.charAt(0) + '.' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {grant.driver.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={`+${grant.credits} credits`} color="success" size="small" />
                  </TableCell>
                  <TableCell>{formatDate(grant.grantedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Package Create/Edit Dialog */}
      <Dialog
        open={packageDialogOpen}
        onClose={closePackageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingPackage ? 'Edit Package' : 'Create New Package'}
            </Typography>
            <IconButton onClick={closePackageDialog} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Package Name"
              value={packageForm.name}
              onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Starter Pack, Pro Bundle"
            />
            <TextField
              label="Number of Credits"
              type="number"
              value={packageForm.rideCount}
              onChange={(e) => setPackageForm({ ...packageForm, rideCount: parseInt(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ min: 1 }}
              helperText="How many ride credits this package includes"
            />
            <TextField
              label="Discount Percentage"
              type="number"
              value={packageForm.discountPercentage}
              onChange={(e) => setPackageForm({ ...packageForm, discountPercentage: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 1 }}
              helperText="Enter 5 for 5% discount, 10 for 10%, etc."
            />
            <TextField
              label="Description (optional)"
              value={packageForm.description}
              onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description shown to drivers"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={packageForm.isPopular}
                  onChange={(e) => setPackageForm({ ...packageForm, isPopular: e.target.checked })}
                />
              }
              label="Mark as Popular (shows 🔥 badge)"
            />

            {/* Price Preview */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Price Preview (based on current base rate: {formatCurrency(baseRate)})
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Original Price</Typography>
                  <Typography variant="body1">
                    {formatCurrency(baseRate * packageForm.rideCount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Final Price</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatCurrency(baseRate * packageForm.rideCount * (1 - packageForm.discountPercentage / 100))}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Savings</Typography>
                  <Typography variant="body1" color="success.main">
                    {packageForm.discountPercentage > 0
                      ? formatCurrency(baseRate * packageForm.rideCount * (packageForm.discountPercentage / 100))
                      : '—'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closePackageDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePackage}
            disabled={savingPackage}
            startIcon={savingPackage ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {savingPackage ? 'Saving...' : editingPackage ? 'Update Package' : 'Create Package'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreditPromotionsPage;

